'use client';

import { createContext, useContext, useMemo } from 'react';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';

export type ServiceTypeTemplate = ApiSchemas['ServiceTypeTemplateRead'];

interface ContextType {
  // Active service type templates, sorted by name
  serviceTypeTemplates: ServiceTypeTemplate[];
  allServiceTypeTemplates: ServiceTypeTemplate[];
  serviceTypeTemplatesById: Map<string, ServiceTypeTemplate>;
  isLoading: boolean;
}

const ServiceTypeTemplatesContext = createContext<ContextType | null>(null);

export function useServiceTypeTemplatesCtx() {
  const ctx = useContext(ServiceTypeTemplatesContext);
  if (!ctx) {
    throw new Error('useServiceTypeTemplatesCtx must be used within a ServiceTypeTemplatesProvider');
  }
  return ctx;
}

export function useServiceTypeTemplateById(id: string) {
  const { serviceTypeTemplatesById } = useServiceTypeTemplatesCtx();
  return serviceTypeTemplatesById.get(id) ?? null;
}

interface Props {
  children: React.ReactNode;
}

export function ServiceTypeTemplatesProvider({ children }: Props) {
  const { api } = useApiV1Ctx();
  const { data: serviceTypeTemplates = [], isLoading } =
    api.serviceTypeTemplates.getAllServiceTypeTemplatesGet.useQuery(undefined, {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const [activeServiceTypeTemplates, serviceTypeTemplatesById] = useMemo(() => {
    const active = serviceTypeTemplates
      .filter((serviceTemplate) => serviceTemplate.status === 'ACTIVE')
      .toSorted((a, b) => a.name.localeCompare(b.name));
    const byId = new Map(serviceTypeTemplates.map((serviceTemplate) => [serviceTemplate.id, serviceTemplate]));
    return [active, byId];
  }, [serviceTypeTemplates]);

  return (
    <ServiceTypeTemplatesContext.Provider
      value={{
        serviceTypeTemplates: activeServiceTypeTemplates,
        allServiceTypeTemplates: serviceTypeTemplates,
        serviceTypeTemplatesById,
        isLoading,
      }}
    >
      {children}
    </ServiceTypeTemplatesContext.Provider>
  );
}
